import React, { useState, useEffect } from 'react';
import { collection, getDocs, deleteDoc, doc, query, orderBy } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Button } from '@/components/ui/Button';
import { AddAnnouncementModal } from './AddAnnouncementModal';

export const AnnouncementsManager: React.FC = () => {
    const [announcements, setAnnouncements] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState<any>(null);

    const fetchData = async () => {
        setLoading(true);
        try {
            const q = query(collection(db, 'announcements'), orderBy('publishedAt', 'desc'));
            const sn = await getDocs(q);
            setAnnouncements(sn.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (err) {
            console.error("Error fetching announcements:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleDelete = async (id: string) => {
        if (!confirm('Are you sure you want to delete this announcement?')) return;
        try {
            await deleteDoc(doc(db, 'announcements', id));
            fetchData();
        } catch (err) {
            console.error("Error deleting announcement:", err);
        }
    };

    const handleEdit = (item: any) => {
        setEditingItem(item);
        setIsModalOpen(true);
    };

    if (loading) return <div className="p-8">Loading announcements...</div>;

    return (
        <div className="space-y-6 font-sans">
            <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold font-serif text-secondary">Announcements</h3>
                <Button onClick={() => { setEditingItem(null); setIsModalOpen(true); }} size="sm">New Announcement</Button>
            </div>

            <div className="grid grid-cols-1 gap-4">
                {announcements.map((a) => (
                    <div key={a.id} className={`p-6 bg-white rounded-xl shadow-card border border-gray-100 border-l-4 ${a.isPublished ? 'border-l-primary' : 'border-l-gray-300'} transition-all hover:shadow-md`}>
                        <div className="flex justify-between items-start mb-2">
                            <div>
                                <h4 className="text-lg font-bold text-secondary tracking-tight">{a.title?.en}</h4>
                                <p className="text-sm text-secondary-light font-medium italic mt-0.5">{a.title?.es}</p>
                            </div>
                            <div className="flex gap-1">
                                {a.events?.map((c: string) => (
                                    <span key={c} className="bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded text-[10px] font-black">{c}</span>
                                ))}
                                {!a.isPublished && (
                                    <span className="bg-yellow-50 text-yellow-700 border border-yellow-100 px-1.5 py-0.5 rounded text-[10px] font-black ml-1 uppercase">Draft</span>
                                )}
                            </div>
                        </div>
                        <p className="text-base text-secondary line-clamp-2 mt-3">{a.body?.en}</p>
                        <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-50">
                            <span className="text-[10px] text-gray-400 font-medium tracking-wide uppercase">
                                {a.publishedAt?.toDate ? a.publishedAt.toDate().toLocaleDateString() : 'N/A'}
                            </span>
                            <div className="flex gap-4">
                                <button onClick={() => handleEdit(a)} className="text-primary hover:underline text-sm font-bold uppercase tracking-widest">Edit</button>
                                <button onClick={() => handleDelete(a.id)} className="text-red-500 hover:underline text-sm font-bold uppercase tracking-widest">Delete</button>
                            </div>
                        </div>
                    </div>
                ))}
                {announcements.length === 0 && (
                    <div className="p-12 text-center bg-white rounded-xl border-2 border-dashed border-gray-100">
                        <p className="text-secondary-light italic text-base">No announcements yet. Send your first update to guests!</p>
                    </div>
                )}
            </div>

            <AddAnnouncementModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSuccess={fetchData}
                announcementToEdit={editingItem}
            />
        </div>
    );
};
